import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { AgentsService } from '../../../services/agents.service';
import { DEFAULT_SITE_BUILD_MODE, SiteModeRow } from '../../../models/agents.model';
import { buildModeHint, buildModeLabel, buildModePillClass, errorMessage, shortId } from '../../agents/agents-format';

/**
 * Businesses → profile → "Websites" (specs/agents spec-v1.5 FR-55.8, tasks-v1.5 V5). Read-only: each
 * website's build mode — manual, hybrid or autonomous — who set it and when, from the AI API's admin
 * route `GET api/admin/agents/website-builder/{businessId}/site-modes` (CONTRACT-v1.5 §7). Only the owner
 * (Agents.Configure) changes a mode, in the website builder; support reads it here.
 *
 * The route lists stored rows only: a website with no row builds in hybrid (§7.2 "404 = hybrid by default"),
 * so an empty list — or a 404 from an AI API that predates v1.5 — is the empty state, never an error. Agents
 * dark on the AI API (503) or no answer at all shows one quiet line, as the Plus Intelligence panel does.
 */
@Component({
  selector: 'app-business-websites-panel',
  standalone: false,
  templateUrl: './websites-panel.component.html',
  styleUrls: ['../intelligence-panel/intelligence-panel.component.css', './websites-panel.component.css']
})
export class BusinessWebsitesPanelComponent implements OnChanges {
  @Input() businessId?: string;

  rows: SiteModeRow[] = [];
  loading = false;
  error = '';
  /** Set when the AI API answers 503 (agents switched off there) or not at all; replaces the panel body. */
  unavailable = '';

  readonly modeLabel = buildModeLabel;
  readonly modeHint = buildModeHint;
  readonly modePill = buildModePillClass;
  readonly defaultModeLabel = buildModeLabel(DEFAULT_SITE_BUILD_MODE);

  constructor(private agents: AgentsService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['businessId']) this.load();
  }

  load(): void {
    this.rows = [];
    this.error = '';
    this.unavailable = '';
    const id = this.businessId;
    if (!id) return;

    this.loading = true;
    this.agents.getSiteModes(id).subscribe({
      next: rows => {
        this.rows = Array.isArray(rows) ? rows : [];
        this.loading = false;
      },
      error: err => {
        this.loading = false;
        if (err?.status === 404) return; // no stored modes (or a pre-v1.5 AI API): the empty state
        if (err?.status === 503) {
          this.unavailable = 'Agents are switched off on the AI API.';
          return;
        }
        if (err?.status === 0) {
          this.unavailable = 'The AI API did not answer (network or CORS).';
          return;
        }
        this.error = errorMessage(err, 'Could not load the websites’ build modes.');
      }
    });
  }

  /** The website's name when the AI API resolves it, else its short id. */
  siteLabel(row: SiteModeRow): string {
    const name = row.siteName?.trim();
    if (name) return name;
    return row.siteId ? shortId(row.siteId) : '—';
  }

  /** A row with no mode reads as the default the platform applies (§7.2). */
  modeOf(row: SiteModeRow): string {
    return row.mode || DEFAULT_SITE_BUILD_MODE;
  }

  get autonomousCount(): number {
    return this.rows.filter(r => r.mode === 'autonomous').length;
  }
}
